import {
  PRICE_AXIS_PADDING_X_PX,
  PRICE_BADGE_ARROW_WIDTH_PX,
  PRICE_BADGE_HEIGHT_PX,
  PRICE_BADGE_RADIUS_PX,
  PRICE_BADGE_SHOULDER_RADIUS_PX,
} from "../constants";

const HALF_HEIGHT = PRICE_BADGE_HEIGHT_PX / 2;

/**
 * Centre of a badge for a price at `y`, kept inside the pane height.
 *
 * @param {number} y - CSS px of the pane
 * @param {{ height: number }} pane
 * @returns {number}
 */
export const clampBadgeY = (y, pane) =>
  Math.min(Math.max(y, HALF_HEIGHT), Math.max(HALF_HEIGHT, pane.height - HALF_HEIGHT));

/**
 * Rectangle a badge takes on the price axis, for moving badges apart.
 *
 * @param {number} centreY
 * @param {{ width: number }} pane
 * @param {number} priceAxisWidth
 * @returns {{ x: number, y: number, width: number, height: number }}
 */
export const priceBadgeRect = (centreY, pane, priceAxisWidth) => ({
  x: pane.width,
  y: centreY - HALF_HEIGHT,
  width: priceAxisWidth,
  height: PRICE_BADGE_HEIGHT_PX,
});

/**
 * Where the text of a badge starts: after its point and the axis padding.
 *
 * @param {{ x: number }} rect - from priceBadgeRect
 * @returns {number} CSS px
 */
export const priceBadgeTextX = (rect) => rect.x + PRICE_BADGE_ARROW_WIDTH_PX + PRICE_AXIS_PADDING_X_PX;

/**
 * Price badge on the price axis, the same for the last price, the crosshair,
 * the entry and pinned position lines (moon price-badge-zoom.png): a tag
 * whose sharp point on the left touches the price line, a fully round right
 * end and slightly rounded corners next to the point, the price after the
 * point like the axis labels.
 *
 * @param {object} textLayer
 * @param {{ x: number, y: number, width: number, height: number }} rect - from priceBadgeRect
 * @param {string} text
 * @param {{ font: string, background: string, color: string, textIndent?: number }} style - CSS colours;
 *   textIndent: CSS px left free before the text (e.g. for ▲ / ▼)
 */
export const drawPriceBadge = (textLayer, rect, text, { font, background, color, textIndent = 0 }) => {
  const { context } = textLayer;
  const { x, y, width, height } = rect;
  const bodyX = x + PRICE_BADGE_ARROW_WIDTH_PX;
  const right = x + width;
  const bottom = y + height;
  const centreY = y + height / 2;
  const radius = Math.min(PRICE_BADGE_RADIUS_PX, height / 2);

  context.beginPath();
  context.moveTo(x, centreY);
  context.arcTo(bodyX, y, right, y, PRICE_BADGE_SHOULDER_RADIUS_PX);
  context.arcTo(right, y, right, bottom, radius);
  context.arcTo(right, bottom, bodyX, bottom, radius);
  context.arcTo(bodyX, bottom, x, centreY, PRICE_BADGE_SHOULDER_RADIUS_PX);
  context.closePath();
  context.fillStyle = background;
  context.fill();
  textLayer.drawText(text, priceBadgeTextX(rect) + textIndent, centreY, { font, color, align: "left" });
};
